import { Injectable } from '@angular/core';
import { Observable, forkJoin, map } from 'rxjs';

import { RestApiService } from '../../../core/api/rest-api.service';
import { ReportQuery, ReportQueryService } from '../../../core/services/report-query';
import { formatString } from '../../../core/utils/string.utils';
import {
  POS_RECEIPT_EMAIL_ENDPOINT, POS_RECEIPT_ENDPOINT, POS_RECEIPT_INVOICE_ENDPOINT,
  POS_RECEIPT_WHATSAPP_ENDPOINT, POS_REGISTER_CATEGORIES_ENDPOINT, POS_REGISTER_CUSTOMERS_ENDPOINT,
  POS_REGISTER_SESSION_CHECKOUT_ENDPOINT, POS_REGISTER_SESSION_CLOSE_ENDPOINT,
  POS_REGISTER_SESSIONS_OPEN_ENDPOINT, POS_REGISTER_TERMINAL_ENDPOINT,
  POS_REGISTER_TERMINAL_PRODUCTS_ENDPOINT, POS_REGISTER_TERMINAL_SESSION_ENDPOINT,
  POS_REGISTER_TERMINALS_ENDPOINT,
  POS_ORDER_DETAIL_ENDPOINT, POS_SESSION_DETAIL_ENDPOINT, POS_PAYMENT_DETAIL_ENDPOINT,
  POS_CUSTOMER_DETAIL_ENDPOINT, POS_INVENTORY_PRODUCTS_ENDPOINT, POS_INVENTORY_PRODUCT_ENDPOINT,
  POS_INVENTORY_PRODUCT_FORM_ENDPOINT,
} from '../../../core/api/api-endpoints/point-of-sale.endpoints';

export interface PosPagination { page: number; per_page: number; total: number; total_pages: number; }
export interface PosRecord { id: string; reference?: string; name?: string; status?: string;[key: string]: unknown; }
export interface PosOrderListItem extends PosRecord { created_at: string | number | Date | null; customer_name?: string | null; session_name?: string | null; cashier_name?: string | null; payment_status?: string | null; currency?: string | null; total_amount?: number | string | null; }
export interface PosOrderLine {
  id: string;
  product_name?: string | null;
  quantity: number | string;
  unit_price: number | string;
  discount_percent: number | string;
  tax_amount: number | string;
  total_amount: number | string;
  selected_combo_items?: Array<{ combo_choice_name?: string; item_ids?: string[]; extra_price?: number | string }> | null;
}
export interface PosOrderPayment { id: string; amount: number | string; payment_method_name?: string | null; }
export interface PosOrderDetail extends PosOrderListItem {
  created_at: string | null;
  business_document_id?: string | null;
  cashier_employment_id?: string | null;
  delivery_stock_transfer_business_document_id?: string | null;
  session_id?: string | null;
  receipt_number?: string | null;
  subtotal_amount: number | string | null;
  tax_amount: number | string | null;
  customer?: PosCustomer | null;
  terminal_name?: string | null;
  session_code?: string | null;
  journal_entry_id?: string | null;
  change_amount?: number | string | null;
  lines: PosOrderLine[];
  payments: PosOrderPayment[];
}
export interface PosSessionListItem extends PosRecord { code?: string | null; active?: boolean; session_status?: string | null; opening_policy?: string | null; order_count?: number | string | null; gross_sales?: number | string | null; expected_cash_amount?: number | string | null; cash_expected?: number | string | null; }
export interface PosCustomerDetail extends PosRecord { email?: string | null; phone?: string | null; mobile?: string | null; city?: string | null; country?: string | null; credit_limit?: number | string | null; description?: string | null; }
export interface PosListResponse<T = PosRecord> { records: T[]; page: number; limit: number; total_records: number; total_pages: number; pagination: PosPagination; summary?: { total: number; active: number; archived: number; needs_review: number }; }
export interface PosReportResponse { summary: { currency: string; total_sales: number; order_count: number; average_order: number; refund_amount?: number }; records: PosRecord[]; chart: Array<Record<string, unknown>>; }

export interface PosPaymentMethod { id: string; name: string; code?: string | null; }
export interface PosPricelist { id: string; name: string; currency_id?: string | null; }
export interface PosTerminal {
  id: string;
  name: string;
  code: string;
  branch_id: string;
  outlet_location_id?: string | null;
  requires_approval?: boolean;
  can_manage?: boolean;
  action_permissions?: { remove_line: boolean; deactivate_order: boolean; change_price: boolean; edit_past_order: boolean };
  currency_id?: string | null;
  opening_cash_control_enabled: boolean;
  closing_cash_control_enabled: boolean;
  invoice_enabled: boolean;
  auto_print_receipt: boolean;
  receipt_email_enabled: boolean;
  receipt_whatsapp_enabled: boolean;
  payment_methods: PosPaymentMethod[];
  pricelists: PosPricelist[];
}
export interface PosComboItem { id: string; product_id: string; product_variant_id?: string | null; product_name: string; variant_name?: string | null; extra_price: string | number; sequence: number; }
export interface PosComboChoice { id: string; name: string; description?: string | null; minimum_selections: number; maximum_selections: number; sequence: number; items: PosComboItem[]; }
export interface PosProductVariant { id: string; name: string; price?: string | number | null; }
export interface PosProductStock {
  tracked: boolean;
  configured: boolean;
  available_quantity: string | number | null;
  needs_replenishment: boolean;
  low_stock_threshold: string | number;
  variants: Record<string, string | number>;
}
export interface PosProduct {
  id: string;
  name: string;
  product_type: string;
  internal_reference?: string | null;
  barcode?: string | null;
  category: string;
  category_name?: string | null;
  sales_price: string | number;
  main_image?: string | null;
  pos_color?: string | null;
  variants?: PosProductVariant[] | null;
  combo_choices: PosComboChoice[];
  terminal_stock?: Record<string, PosProductStock>;
  [key: string]: unknown;
}
export interface PosCustomer { id: string; name: string; email?: string | null; phone?: string | null; mobile?: string | null; }
export interface PosRegisterOptions { products: PosProduct[]; categories: Array<{ id: string; name: string; color_label?: string | null }>; customers: PosCustomer[]; }
export interface PosSession extends PosRecord { code: string; business_document_id?: string | null; pipeline_id?: string | null; terminal_id?: string | null; session_status?: string | null; terminal_name?: string | null; cashier_name?: string | null; can_operate?: boolean; is_operator?: boolean; opened_at?: string | null; closed_at?: string | null; expected_cash_amount?: string | number; }
export interface PosReceipt extends PosRecord {
  receipt_number?: string | null;
  currency: string;
  total_amount: string | number;
  subtotal_amount: string | number;
  customer?: PosCustomer | null;
  terminal_name?: string | null;
  session_code?: string | null;
  change_amount: string | number;
  invoice_transaction_business_document_id?: string | null;
  invoice_number?: string | null;
  lines: Array<{ id: string; product_name?: string | null; quantity: string | number; unit_price: string | number; total_amount: string | number }>;
  payments: Array<{ id: string; amount: string | number; payment_method_name?: string | null }>;
}
export interface ReceiptDeliveryResponse { order_id: string; channel: string; destination: string; delivery_url: string; }
export interface ProductListResponse { products: PosProduct[]; page: number; per_page: number; total: number; total_pages: number; }
export interface ProductFormOptions { product_types: Array<{ id: string; name: string }>; product_categories: Array<{ id: string; name: string }>; uom_options: Array<{ id: string; name: string }>; tracking_options: Array<{ id: string; name: string }>; }

@Injectable({ providedIn: 'root' })
export class PosOperationsService {
  constructor(private readonly api: RestApiService, private readonly reportQuery: ReportQueryService) { }

  getTerminals(): Observable<PosTerminal[]> { return this.api.get(POS_REGISTER_TERMINALS_ENDPOINT); }
  getSelectedTerminals(terminalId: string): Observable<PosTerminal> { return this.api.get(formatString(POS_REGISTER_TERMINAL_ENDPOINT, terminalId)); }
  registerCategories(): Observable<PosRegisterOptions['categories']> { return this.api.get(POS_REGISTER_CATEGORIES_ENDPOINT); }
  registerCustomers(): Observable<PosCustomer[]> { return this.api.get(POS_REGISTER_CUSTOMERS_ENDPOINT); }
  registerProducts(terminalId: string): Observable<PosProduct[]> { return this.api.get(formatString(POS_REGISTER_TERMINAL_PRODUCTS_ENDPOINT, terminalId)); }


  currentSession(terminalId: string): Observable<PosSession | null> { return this.api.get(formatString(POS_REGISTER_TERMINAL_SESSION_ENDPOINT, terminalId)); }
  openSession(payload: { terminal_id: string; pipeline_id?: string | null; opening_cash_amount: number; opening_note?: string | null }): Observable<PosSession> { return this.api.post(POS_REGISTER_SESSIONS_OPEN_ENDPOINT, payload); }
  closeSession(sessionId: string, payload: { closing_cash_amount: number; closing_note?: string | null }): Observable<PosSession> { return this.api.post(formatString(POS_REGISTER_SESSION_CLOSE_ENDPOINT, sessionId), payload); }
  checkout(sessionId: string, payload: Record<string, unknown>): Observable<PosReceipt> { return this.api.post(formatString(POS_REGISTER_SESSION_CHECKOUT_ENDPOINT, sessionId), payload); }
  receipt(orderId: string): Observable<PosReceipt> { return this.api.get(formatString(POS_RECEIPT_ENDPOINT, orderId)); }
  createInvoice(orderId: string): Observable<PosReceipt> { return this.api.post(formatString(POS_RECEIPT_INVOICE_ENDPOINT, orderId), {}); }
  emailReceipt(orderId: string): Observable<ReceiptDeliveryResponse> { return this.api.post(formatString(POS_RECEIPT_EMAIL_ENDPOINT, orderId), {}); }
  whatsappReceipt(orderId: string): Observable<ReceiptDeliveryResponse> { return this.api.post(formatString(POS_RECEIPT_WHATSAPP_ENDPOINT, orderId), {}); }

  order(id: string): Observable<PosOrderDetail> { return this.api.get(formatString(POS_ORDER_DETAIL_ENDPOINT, id)); }
  session(id: string): Observable<PosRecord> { return this.api.get(formatString(POS_SESSION_DETAIL_ENDPOINT, id)); }
  payment(id: string): Observable<PosRecord> { return this.api.get(formatString(POS_PAYMENT_DETAIL_ENDPOINT, id)); }
  customer(id: string): Observable<PosCustomerDetail> { return this.api.get(formatString(POS_CUSTOMER_DETAIL_ENDPOINT, id)); }
  report(kind: 'orders' | 'sales-details' | 'payments' | 'sessions'): Observable<PosReportResponse> {
    const source = kind === 'payments' ? 'pos.payments' : kind === 'sessions' ? 'pos.sessions' : 'pos.orders';
    const amountField = kind === 'payments' ? 'amount' : kind === 'sessions' ? 'expected_cash_amount' : 'total_amount';
    const dateField = kind === 'payments' ? 'paid_at' : kind === 'sessions' ? 'created_at' : 'order_time';
    const fields = kind === 'sessions'
      ? ['record_id', 'name', 'code', 'session_status', amountField, dateField]
      : kind === 'payments'
        ? ['record_id', 'reference', 'name', 'status', 'currency', amountField, dateField]
        : ['record_id', 'reference', 'name', 'status', 'currency', amountField, dateField];
    const totalsQuery: ReportQuery = {
      version: 1, source,
      select: [
        { type: 'aggregate', function: 'sum', field: amountField, as: 'total_amount' },
        { type: 'aggregate', function: 'count_distinct', field: 'record_id', as: 'record_count' },
      ], limit: 1,
    };
    const rowsQuery: ReportQuery = {
      version: 1, source,
      select: fields.map(field => ({ type: 'field', field, as: field })),
      order_by: [{ ref: dateField, direction: 'desc' }], limit: 100,
    };
    return forkJoin({ totals: this.reportQuery.execute(totalsQuery), rows: this.reportQuery.execute(rowsQuery) })
      .pipe(map(({ totals, rows }) => {
        const total = Number(totals.rows[0]?.['total_amount'] ?? 0);
        const count = Number(totals.rows[0]?.['record_count'] ?? 0);
        const currency = String(rows.rows[0]?.['currency'] ?? 'TZS');
        return {
          summary: {
            currency, total_sales: total, total_amount: total, order_count: count,
            record_count: count, average_order: count ? total / count : 0,
            average_amount: count ? total / count : 0, refund_amount: 0, review_count: 0,
          },
          records: rows.rows.map(row => ({
            ...row, id: String(row['record_id'] ?? ''),
            display_amount: row[amountField], display_date: row[dateField],
            status: row['status'] ?? row['session_status'],
          })) as PosRecord[],
          chart: [],
        };
      }));
  }
  products(query = '', page = 1, perPage = 25, extra: Record<string, string | number | boolean> = { can_be_sold: true, available_in_pos: true }): Observable<ProductListResponse> { const params = new URLSearchParams(); const search = query.trim(); if (search) params.set('search', search); params.set('page', String(page)); params.set('per_page', String(perPage)); Object.entries(extra).forEach(([key, value]) => params.set(key, String(value))); return this.api.get(POS_INVENTORY_PRODUCTS_ENDPOINT + '?' + params.toString()); }
  product(id: string): Observable<PosProduct> { return this.api.get(formatString(POS_INVENTORY_PRODUCT_ENDPOINT, id)); }
  productFormOptions(): Observable<ProductFormOptions> { return this.api.get(POS_INVENTORY_PRODUCT_FORM_ENDPOINT); }
  createProduct(payload: Record<string, unknown>): Observable<PosProduct> { return this.api.post(POS_INVENTORY_PRODUCTS_ENDPOINT, payload); }
  updateProduct(id: string, payload: Record<string, unknown>): Observable<PosProduct> { return this.api.patch(formatString(POS_INVENTORY_PRODUCT_ENDPOINT, id), payload); }
}
